/**
 * Applicant flow plan — which screens this session shows, in what order.
 *
 * ## Why this exists
 *
 * `ApplicantFlow.tsx` has always rendered a hardcoded six-entry `steps` array,
 * with every content block keyed on `current === N` against that array's
 * indices. ADR-014 makes step inclusion and ORDER configuration: a session
 * carrying a `workflow_key` resolves a `workflow_definitions` row, and
 * `GET /api/v1/sessions/:token` serves its ordered `steps`.
 *
 * ## Parallel path, not a refactor
 *
 * This module builds a *plan* — an ordered list of the underlying step indices
 * to show. The legacy plan is exactly today's `visibleStepIndices`, so the
 * Rockwell/leasing journey renders through the same code with the same
 * numbering, the same next-step walk and the same stepper pips it had before.
 * The definition path is a second plan over the SAME content blocks.
 *
 * The indices are deliberately still the load-bearing thing. Rewriting the
 * `current === N` blocks to key off step types would touch every branch of a
 * 2800-line component that the pilot depends on, a week before the demo. This
 * reorders and re-labels; it does not restructure.
 *
 * WARNING: `'standard'` is treated as NO key, mirroring the back end exactly
 * (`getSessionProgress` in verification-sessions.ts). `POST /inquiries` takes
 * `workflow_key` from the request body unvalidated, so the literal string is
 * writable, and it must not move a live org onto this path.
 */
import type { LucideIcon } from "lucide-react";
import { FileCheck, Link2, Mail, ScanFace, Shield, UserPlus } from "lucide-react";
import type { WorkflowStep } from "@/lib/api";

/**
 * Underlying indices into `ApplicantFlow`'s `steps` array. These are the
 * `current === N` keys and are load-bearing — never renumber them. IDV is 6
 * (appended) rather than slotted in after consent for exactly that reason.
 */
export const APPLICANT_STEP = {
  CONSENT: 0,
  EMAIL: 1,
  ACCOUNTS: 2,
  /** "Connect a work or income source" — hidden since 2026-05-20. */
  WORK: 3,
  VOUCH: 4,
  REVIEW: 5,
  /** Plaid IDV as its own screen. Definition-driven flows only. */
  IDV: 6,
} as const;

export interface FlowPlanEntry {
  /** Underlying index into the `steps` array — what `current` is compared to. */
  index: number;
  title: string;
  desc: string;
  icon: LucideIcon;
  /** True when the definition marks the step optional. Presentational only. */
  optional?: boolean;
  /**
   * The definition's `step_type` for this entry, when it came from one.
   *
   * Absent on every legacy-plan entry and on the two trailing screens, which
   * are not workflow steps and therefore cannot appear in `steps_completed`.
   * Its presence is exactly the test for "server evidence can speak to this
   * screen" — see `isPlanEntrySatisfied`.
   */
  stepType?: string;
  /** The definition's `satisfied_by` equivalences, verbatim. */
  satisfiedBy?: string[];
}

/**
 * `step_type` -> presentation. The definition carries inclusion and ORDER and
 * nothing else — no title, no icon, no copy — so this map is where the
 * applicant-facing wording lives.
 *
 * ## It is NOT recruiting-flavoured, and that is now enforced
 *
 * This map's comment used to say it *"is recruiting-flavoured because the only
 * flows that reach it are IDW's"*, and the vertical-aware case model recorded
 * that as proxy 4 — a vertical decision made implicitly, which the first
 * leasing org given a bespoke flow would break.
 *
 * Read string by string against the leasing flow it would replace, the claim
 * does not hold: `consent` and `email` are **byte-identical** to the legacy
 * leasing entries in `ApplicantFlow`'s `steps` array, and `plaid_idv`,
 * `social` and both trailing screens contain no vertical vocabulary at all.
 * There is no recruiting word here to key off a vertical, so this map does not
 * take one — `verticalCopy.ts`'s own rule is that a copy key with nothing to
 * hold is just an indirection.
 *
 * What replaces the implicit proxy is an explicit invariant:
 * `__tests__/flowPlanVocabulary.test.ts` scans every string below for leasing
 * *and* recruiting vocabulary and fails on either. So the map cannot drift into
 * being vertical-specific by accident; the day a step genuinely needs different
 * words per vertical, that test goes red and the vertical has to be threaded in
 * deliberately — which is cheap now, because `ApplicantFlow` holds it.
 *
 * ⚠️ **The real hazard is a different axis: optional vs required.** Legacy
 * leasing words its digital-account step for an *optional* step (*"If helpful,
 * you can connect additional accounts…"*) and `social` below words it for a
 * required one. `FlowPlanEntry.optional` already carries that fact and nothing
 * here reads it. Left alone deliberately — changing it would re-word the
 * demo lane where `social` is optional — but it is the thing to fix before a
 * leasing org reaches this map, not the vocabulary.
 *
 * A step type absent from this map has no screen (`phone` is the live example).
 * `buildDefinitionPlan` skips it rather than throwing — see the note there.
 *
 * Exported READ-ONLY, and only so the invariant above can be checked. Its
 * entries are spread into every plan `buildDefinitionPlan` returns, so a
 * mutation here would rewrite the screen list for every later session in the
 * tab.
 */
export const STEP_TYPE_PRESENTATION: Readonly<
  Record<string, Readonly<Omit<FlowPlanEntry, "optional">>>
> = {
  consent: {
    index: APPLICANT_STEP.CONSENT,
    title: "Confirm identity details",
    desc: "Review and confirm the information you've already provided.",
    icon: Shield,
  },
  plaid_idv: {
    index: APPLICANT_STEP.IDV,
    title: "What you'll need",
    desc: "A valid government-issued ID and a quick photo check.",
    icon: ScanFace,
  },
  email: {
    index: APPLICANT_STEP.EMAIL,
    title: "Verify your email",
    desc: "Confirm the email on your application is yours.",
    icon: Mail,
  },
  social: {
    index: APPLICANT_STEP.ACCOUNTS,
    title: "Connect a digital account",
    desc: "Connect an account that shows real history behind your name.",
    icon: Link2,
  },
};

/**
 * Screens every flow ends with, appended after the definition's steps.
 *
 * WARNING: neither is in the definition, and that is on purpose, not an
 * oversight. ADR-014 D6 proposes `vouch_invite` as a real step but a step id is
 * a frozen persisted string under ADR-013 D1, so it is deliberately NOT being
 * named under demo deadline pressure — the vouch leg runs the existing path.
 * Review-and-submit is a UI affordance and was never a workflow step.
 *
 * Consequence: the rendered screen count is the definition's step count plus
 * these two, so `progress.steps_total` is NOT the stepper's denominator. See
 * the note on `planTotalLabel` below.
 */
export const TRAILING_ENTRIES: readonly Readonly<FlowPlanEntry>[] = [
  {
    index: APPLICANT_STEP.VOUCH,
    title: "Invite someone who knows you",
    desc: "Invite someone who can confirm they know you.",
    icon: UserPlus,
  },
  {
    index: APPLICANT_STEP.REVIEW,
    title: "Review and submit",
    desc: "Review everything you've provided before submitting.",
    icon: FileCheck,
  },
];

/**
 * Does this session render from a definition?
 *
 * Null (every session predating the column, i.e. all leasing traffic) and the
 * literal `'standard'` both mean no. Same guard as the back end.
 */
export function isDefinitionDriven(workflowKey: string | null | undefined): boolean {
  return Boolean(workflowKey) && workflowKey !== "standard";
}

/**
 * Build the ordered plan for a definition-driven session, or `null` when the
 * caller should use the legacy plan.
 *
 * Returns null — i.e. falls back to the hardcoded flow — when the session
 * carries no usable key, when the server sent no steps, or when none of the
 * steps it sent map to a screen. Falling back is the safe direction: the legacy
 * flow is complete and submittable, so a definition we cannot render costs
 * ordering, not the applicant's ability to finish.
 */
export function buildDefinitionPlan(
  workflowKey: string | null | undefined,
  steps: readonly WorkflowStep[] | null | undefined,
): FlowPlanEntry[] | null {
  if (!isDefinitionDriven(workflowKey)) return null;
  if (!steps || steps.length === 0) return null;

  const entries: FlowPlanEntry[] = [];
  const claimed = new Set<number>();

  for (const step of steps) {
    const presentation = STEP_TYPE_PRESENTATION[step.step_type];
    if (!presentation) {
      // A registered step type with no screen (`phone`). Skipping is visible in
      // the console and pinned by a test over the seeded IDW definitions, which
      // is the actual guard — a required step silently vanishing from the flow
      // is worth catching before a stage, not at runtime.
      console.warn(
        `[flowPlan] workflow "${workflowKey}" names step_type "${step.step_type}", which has no screen. Skipping.`,
      );
      continue;
    }
    // Two definition steps sharing a screen would render the same index twice
    // and break the rank ordering below. First one wins.
    if (claimed.has(presentation.index)) continue;
    claimed.add(presentation.index);
    entries.push({
      ...presentation,
      optional: !step.required,
      stepType: step.step_type,
      satisfiedBy: step.satisfied_by,
    });
  }

  if (entries.length === 0) return null;

  for (const trailing of TRAILING_ENTRIES) {
    if (!claimed.has(trailing.index)) entries.push(trailing);
  }

  return entries;
}

/**
 * Where an underlying step index sits in the plan, as a sortable rank.
 *
 * Members rank by position. A NON-member ranks strictly between the members
 * either side of it in index order — the legacy plan omits index 3 (the hidden
 * work step) while the backend's `current_step` counter can still land on it,
 * and every `<` / `<=` / `Math.max` over indices in `ApplicantFlow` has to keep
 * the exact ordering it had when it compared raw indices.
 *
 * That property holds because the legacy plan is index-ascending: ranking
 * 0,1,2,3,4,5 gives 0,1,2,2.5,3,4 — strictly increasing, so every comparison is
 * order-isomorphic to comparing the indices themselves. Legacy behavior is
 * unchanged by construction rather than by inspection.
 */
export function rankOfIndex(planIndices: readonly number[], idx: number): number {
  const pos = planIndices.indexOf(idx);
  if (pos !== -1) return pos;
  let below = 0;
  for (const planned of planIndices) {
    if (planned < idx) below++;
  }
  return below - 0.5;
}

/** The plan's later of two step indices. Replaces `Math.max` over raw indices. */
export function laterInPlan(planIndices: readonly number[], a: number, b: number): number {
  return rankOfIndex(planIndices, a) >= rankOfIndex(planIndices, b) ? a : b;
}

/**
 * The step index after `idx` in plan order, clamped to the last step.
 *
 * Replaces `nextVisibleStep`'s forward walk over the array, which assumed plan
 * order and index order were the same thing. They are not, once a definition
 * can put IDV (index 6) second.
 */
export function nextIndexInPlan(planIndices: readonly number[], idx: number): number {
  const rank = rankOfIndex(planIndices, idx);
  for (let pos = 0; pos < planIndices.length; pos++) {
    if (pos > rank) return planIndices[pos];
  }
  return planIndices[planIndices.length - 1];
}

/** True when `idx` is the plan's final step — where "Continue" stops advancing. */
export function isLastInPlan(planIndices: readonly number[], idx: number): boolean {
  return rankOfIndex(planIndices, idx) >= planIndices.length - 1;
}

/**
 * Is this plan entry satisfied by what the server has recorded?
 *
 * Mirrors the back end's `isStepSatisfied` (`services/workflow/definitions.ts`)
 * exactly — the step's own type, or ANY of its `satisfied_by` equivalences.
 * Keeping the two identical is the whole point: the applicant's stepper and the
 * server's gate must not be able to disagree about whether a step is done.
 *
 * An entry with no `stepType` (the two trailing screens; every legacy entry) is
 * never satisfied this way. `steps_completed` cannot contain it, so a false
 * here means "server evidence has nothing to say", not "the applicant skipped
 * it" — callers handle those screens separately.
 */
export function isPlanEntrySatisfied(
  entry: FlowPlanEntry,
  completed: ReadonlySet<string>,
): boolean {
  if (!entry.stepType) return false;
  if (completed.has(entry.stepType)) return true;
  return (entry.satisfiedBy ?? []).some((alt) => completed.has(alt));
}

/**
 * How far the applicant has PROVABLY reached, from durable server state alone.
 *
 * ## Why this replaces reading `current_step`
 *
 * `session.current_step` comes from `GET /sessions/:token/status`, where it is
 * not a counter and not a plan position — it is a four-value bucket built for
 * the legacy six-step leasing model (`routes/sessions.ts`):
 *
 *   no consent          -> 1   // legacy INDEX 0, consent
 *   nothing verified    -> 2   // legacy INDEX 1, email
 *   finished            -> 6   // legacy INDEX 5, review
 *   otherwise           -> 3   // legacy INDEX 2, social
 *
 * Read as an index that is right, which is why the legacy path still does it.
 * Read as a plan POSITION it is right only for a plan whose indices ascend in
 * step with the legacy layout — `high_volume` does, `tech_role` does not, and
 * on `tech_role` every bucket except the two endpoints resolved to the wrong
 * screen: 2 landed on the IDV screen and 3 on email, so ANY OAuth connection
 * (which moves the bucket to 3) threw the applicant back to "Verify your email"
 * with screens 4-6 unreachable.
 *
 * The bucket also saturates: for `tech_role` it cannot exceed 3 until every
 * REQUIRED step is satisfied, and `plaid_idv` is required — so an applicant who
 * cannot complete IDV never advances by server state at all.
 *
 * ## What this computes instead
 *
 * The entry immediately after the LAST satisfied definition step, in plan
 * order. That is the furthest point server evidence can justify: it survives
 * the full page reload an OAuth redirect causes (where `liveSkippedThrough`
 * resets to 0), it is monotonic because `steps_completed` only grows, and it
 * needs no sessionStorage hand-off to be correct.
 *
 * LAST satisfied rather than FIRST unsatisfied on purpose. A flow can leave an
 * earlier step unsatisfied and carry on — `tech_role`'s IDV when the scan
 * fails, `high_volume`'s optional digital account when it is skipped — and
 * parking the frontier on it would strand the applicant on a step they have
 * already been past. The stepper renders those as amber "unfilled", which is
 * what that state is for.
 *
 * `finished` is the one thing `steps_completed` cannot express here: the vouch
 * and review screens are not workflow steps (ADR-014 D6), so no evidence names
 * them. The caller passes the server's own answer.
 */
export function frontierIndexInPlan(
  plan: readonly FlowPlanEntry[],
  completed: ReadonlySet<string>,
  opts?: { finished?: boolean },
): number {
  const planIndices = plan.map((entry) => entry.index);
  if (planIndices.length === 0) return 0;
  if (opts?.finished) return planIndices[planIndices.length - 1];

  let lastSatisfiedPos = -1;
  for (let pos = 0; pos < plan.length; pos++) {
    if (isPlanEntrySatisfied(plan[pos], completed)) lastSatisfiedPos = pos;
  }
  // Nothing recorded yet — the applicant is at the top of the flow.
  if (lastSatisfiedPos === -1) return planIndices[0];
  return nextIndexInPlan(planIndices, planIndices[lastSatisfiedPos]);
}

/**
 * Which plan entries the server has evidence for, keyed by underlying index.
 *
 * Feeds the stepper's green-vs-amber decision on a definition plan. Replaces a
 * hardcoded provider->step map that predates ADR-014 and had drifted out of
 * agreement with the definitions it was describing: it read Amazon as email
 * evidence and refused it as digital-account evidence, while `tech_role` v2
 * (migration `1787634000000`) makes the two sets disjoint the other way —
 * email is Google/Microsoft/Apple, digital account is LinkedIn/Amazon. An
 * applicant connecting Amazon for the required digital-account step therefore
 * turned the EMAIL pip green and left the digital-account pip amber, while the
 * server counted the step satisfied.
 *
 * Reading the definition's own `satisfied_by` makes that class of drift
 * impossible rather than merely fixed.
 *
 * Only entries the definition contributed appear. The trailing screens are the
 * caller's business — see `isPlanEntrySatisfied`.
 */
/**
 * Is this session's `plaid_idv` step satisfied?
 *
 * The ONE predicate for that question, because two surfaces were answering it
 * differently and disagreeing on screen.
 *
 * Two independent facts can each establish it, and a surface that reads only
 * one of them is wrong for some org:
 *
 *   - **`applicant_idv_verified`** — the attestation lookup. The backend runs
 *     it only when the org config flag `applicant_idv_required` is set
 *     (ADR-012 R3). A definition-driven org requires IDV through its *workflow
 *     definition* and need not set that flag, so this stays `false` for them
 *     however many times the applicant passes.
 *   - **`steps_completed` containing `plaid_idv`** — written on a capture that
 *     actually PASSED, by whichever of the client call, the `STATUS_UPDATED`
 *     webhook, or the reconciliation sweep gets there first.
 *
 * Neither is sufficient alone. The second is absent for the moment before the
 * step write lands (it sits outside the capture transaction and is allowed to
 * fail); the first is permanently absent on a definition-driven flow.
 *
 * ⚠️ The live defect this closes: on a `tech_role` session the stepper pip read
 * both facts and went green, while the IDV card read only `applicant_idv_verified`
 * and rendered an enabled "Verify your identity" button over an IDV that had
 * already passed. Same session, same render, two answers.
 */
export function isIdvSatisfied(session: {
  steps_completed?: string[] | null;
  applicant_idv_verified?: boolean | null;
}): boolean {
  return (
    session.applicant_idv_verified === true ||
    (session.steps_completed ?? []).includes("plaid_idv")
  );
}

export function definitionEvidenceByIndex(
  plan: readonly FlowPlanEntry[],
  completed: ReadonlySet<string>,
): Record<number, boolean> {
  const map: Record<number, boolean> = {};
  for (const entry of plan) {
    if (!entry.stepType) continue;
    map[entry.index] = isPlanEntrySatisfied(entry, completed);
  }
  return map;
}

/**
 * The stepper's "step N of M" denominator.
 *
 * Deliberately the count of screens the applicant is shown, NOT
 * `progress.steps_total`. The two answer different questions and the demo plan
 * asks for the server's number, so this is the one place that guidance does not
 * survive contact with the flow:
 *
 *   tech_role definition steps      = 4  (consent, plaid_idv, email, social)
 *   progress.steps_total            = 4  (what the server counts as progress)
 *   screens the applicant walks     = 6  (+ vouch invite, + review and submit)
 *
 * `steps_total` cannot be the pip count while the last two screens exist
 * outside the definition — it would render "step 6 of 4" on the review screen.
 * Nothing here derives progress semantics from `steps.length`, which is the
 * mistake that guidance is actually guarding against; the server's number stays
 * the authority on how far along the applicant IS.
 */
export function planTotalLabel(planIndices: readonly number[]): number {
  return planIndices.length;
}

/**
 * "Step N of M" for any look, counted over the screens we render. A screen
 * the applicant passed through before reaching us (an ATS application, say)
 * is not ours to count, so there is no `+ 1` here: a flow entered another way
 * would be off by one. Both the original stepper and the Article meta row
 * read this one helper.
 *
 * `position` is 1-based. An index outside the plan clamps to the first screen,
 * which is what the meta row has always shown for it.
 */
export function stepCounter(
  planIndices: readonly number[],
  idx: number,
): { position: number; total: number } {
  return {
    position: Math.max(0, planIndices.indexOf(idx)) + 1,
    total: planTotalLabel(planIndices),
  };
}

/**
 * Does this screen offer "Skip this step"? The same answer for every look: a
 * definition says which steps are optional, so Skip follows that flag
 * (`tech_role` REQUIRES the digital-account step, and offering Skip there
 * would contradict the flow the org configured); the legacy plan keeps its
 * index test. Never on consent, the vouch step (there is no bypass for the
 * invite requirement) or the last screen.
 */
export function offersSkipStep(
  planIndices: readonly number[],
  idx: number,
  step: { definitionDriven: boolean; optional: boolean | undefined },
): boolean {
  const byPlan = step.definitionDriven
    ? !isLastInPlan(planIndices, idx) && step.optional === true
    : idx < APPLICANT_STEP.REVIEW;
  return byPlan && idx !== APPLICANT_STEP.CONSENT && idx !== APPLICANT_STEP.VOUCH;
}
